/**
 * Subsystems: the GUI's services, started for you in the right order, like a game engine's
 * subsystems (Unreal's `UGameInstanceSubsystem`, for instance).
 *
 * Each subsystem is declared once, as data:
 *
 * ```ts
 * define({
 *   id: "project",
 *   summary: "The open project: name, file, saved or not",
 *   needs: ["client", "graph"],
 *   start: ({ client, graph }) => createProject(client, graph),
 *   commands: (project) => projectCommands(project),
 * })
 * ```
 *
 * {@link startSubsystems} then starts every one after the ones it needs, gives each only what
 * it declared, collects their commands, and stops them in reverse order. A need nobody
 * provides, two subsystems with the same id, or subsystems needing each other in a circle are
 * reported at once, by name, instead of showing up later as `undefined`.
 *
 * This file knows nothing about the app: `All` is the record of every subsystem's value (the
 * app's is `App` in `app/context.ts`, its list `app/subsystems.ts`).
 */
import type { CommandSet } from "./commands";
import type { MenuSection, ToolbarButton } from "./menus";

/** One subsystem of `All`: its id `K`, built from the subsystems `N` it needs. */
export interface Subsystem<All, K extends keyof All = keyof All, N extends keyof All = keyof All> {
  /** Its name, and where its value goes in `All`. */
  id: K;
  /** One line saying what it does (shown by the debug listing). */
  summary: string;
  /** The subsystems (or given values) it is built from. */
  needs: readonly N[];
  /** Builds it, from exactly what it needs. */
  start(needs: Pick<All, N>): All[K];
  /** Its named commands, for shortcuts and menus (`shared/commands.ts`). */
  commands?(self: All[K], needs: Pick<All, N>): CommandSet;
  /** Its sections of the menu bar's menus (`shared/menus.ts`). */
  menus?(self: All[K], needs: Pick<All, N>): MenuSection[];
  /** Its toolbar buttons (`shared/menus.ts`). */
  toolbar?(self: All[K], needs: Pick<All, N>): ToolbarButton[];
  /** Lets go of what it holds (connections, listeners). Called in reverse start order. */
  stop?(self: All[K]): void;
}

/**
 * Returns a `define` function that checks a subsystem of `All` with full types: `start` sees
 * only the needs it lists, and must return the value `All` has under its id.
 */
export function subsystemsOf<All>() {
  return <K extends keyof All, N extends keyof All = never>(subsystem: Subsystem<All, K, N>): Subsystem<All, K, N> => subsystem;
}

/** The subsystems once started. */
export interface Started<All> {
  /** Every value, by id (the given ones included). */
  all: All;
  /** The ids in the order they started. */
  order: (keyof All)[];
  /** The command sets the subsystems contributed, in start order. */
  commands: CommandSet[];
  /** The menu sections they contributed, in start order. */
  menus: MenuSection[];
  /** The toolbar buttons they contributed, in start order. */
  toolbar: ToolbarButton[];
  /** Stops every subsystem that can be stopped, last started first. */
  stop(): void;
}

/**
 * Starts `subsystems` in an order where each one comes after what it needs. `given` holds the
 * values that come from outside (the platform, how to reach the core...); a subsystem may need
 * them like any other.
 *
 * Throws, naming the culprits, when an id is used twice, a need is provided by nobody, or
 * needs form a circle.
 */
export function startSubsystems<All>(subsystems: readonly Subsystem<All, keyof All, keyof All>[], given: Partial<All> = {}): Started<All> {
  const order = startOrder(subsystems, Object.keys(given) as (keyof All)[]);
  const all = { ...given } as All;
  const commands: CommandSet[] = [];
  const menus: MenuSection[] = [];
  const toolbar: ToolbarButton[] = [];
  const started: Subsystem<All, keyof All, keyof All>[] = [];
  for (const subsystem of order) {
    const needs = pick(all, subsystem.needs);
    const value = subsystem.start(needs);
    all[subsystem.id] = value;
    started.push(subsystem);
    const set = subsystem.commands?.(value, needs);
    if (set) commands.push(set);
    menus.push(...(subsystem.menus?.(value, needs) ?? []));
    toolbar.push(...(subsystem.toolbar?.(value, needs) ?? []));
  }
  return {
    all,
    order: order.map((s) => s.id),
    commands,
    menus,
    toolbar,
    stop() {
      for (const subsystem of [...started].reverse()) subsystem.stop?.(all[subsystem.id]);
    },
  };
}

/**
 * The subsystems sorted so that each comes after everything it needs (`given` ids count as
 * already there). Keeps the listed order where needs allow it, so the start order is easy to
 * predict when reading the list.
 */
export function startOrder<All>(subsystems: readonly Subsystem<All, keyof All, keyof All>[], given: readonly (keyof All)[] = []): Subsystem<All, keyof All, keyof All>[] {
  const byId = new Map<keyof All, Subsystem<All, keyof All, keyof All>>();
  for (const subsystem of subsystems) {
    if (byId.has(subsystem.id) || given.includes(subsystem.id)) throw new Error(`subsystem "${String(subsystem.id)}" is declared twice`);
    byId.set(subsystem.id, subsystem);
  }
  const missing = subsystems.flatMap((s) => s.needs.filter((n) => !byId.has(n) && !given.includes(n)).map((n) => `"${String(s.id)}" needs "${String(n)}"`));
  if (missing.length) throw new Error(`nobody provides what these subsystems need: ${missing.join(", ")}`);

  const done = new Set<keyof All>(given);
  const order: Subsystem<All, keyof All, keyof All>[] = [];
  let waiting = [...subsystems];
  while (waiting.length) {
    // The first one in the list whose needs are all started: the list's order where possible.
    const next = waiting.find((s) => s.needs.every((n) => done.has(n)));
    if (!next) {
      const stuck = waiting.map((s) => `${String(s.id)} → ${s.needs.filter((n) => !done.has(n)).map(String).join(", ")}`);
      throw new Error(`these subsystems need each other in a circle: ${stuck.join("; ")}`);
    }
    order.push(next);
    done.add(next.id);
    waiting = waiting.filter((s) => s !== next);
  }
  return order;
}

/** Only the `keys` of `all`: a subsystem gets nothing it did not ask for. */
function pick<All, N extends keyof All>(all: All, keys: readonly N[]): Pick<All, N> {
  const out = {} as Pick<All, N>;
  for (const key of keys) out[key] = all[key];
  return out;
}
